import React, { useEffect, useState } from "react";
import { Flame, Copy, TrendingUp, TrendingDown } from "lucide-react";
import { api, inr } from "@/lib/api";
import { StockMenu, WatchAdd } from "@/components/WatchAdd";
import { copyTv } from "@/pages/VaCross";
import { BeivPanel } from "@/components/BeivPanel";
import { useFlow, FlowTags, InTime } from "@/components/FlowMarks";

const ZONE = { D_narrow: "NZ D", D_inside: "NZ IN D", W_narrow: "NZ W", W_inside: "NZ IN W", M_narrow: "NZ M", M_inside: "NZ IN M" };
const tag = "ml-1 text-[9px] px-1 font-bold";
const chip = (on) => `h-7 px-3 text-xs font-mono border transition-colors ${on ? "bg-amber-400 text-black border-amber-400 font-bold" : "border-slate-300 text-slate-500 hover:border-amber-400"}`;
const fmtV = (v) => (v >= 1e7 ? `${(v / 1e7).toFixed(2)}Cr` : v >= 1e5 ? `${(v / 1e5).toFixed(2)}L` : v?.toLocaleString("en-IN"));
const pct = (v) => (v == null ? "—" : <span className={v >= 0 ? "text-emerald-600" : "text-rose-600"}>{v > 0 ? "+" : ""}{v}%</span>);

const Row = ({ r, side, hot, m }) => {
  const V = side === "up" ? "Node-H" : "Node-L";
  return (
    <div className="flex items-center gap-1 border-b border-slate-200">
      <StockMenu instrumentId={r.instrument_id} symbol={r.symbol}>
        <button className={`flex-1 min-w-0 flex items-center gap-2 text-xs font-mono px-2 py-1.5 hover:bg-blue-500/10 transition-colors text-left ${(r.open_x || 0) >= 3 ? "border-l-2 border-amber-400" : r.zone?.length ? "border-l-2 border-violet-400" : ""}`} data-testid={`av-row-${side}-${r.symbol}`}>
          <span className="flex-1 min-w-0 text-slate-900 font-semibold">{r.symbol}
            <span className="ml-1"><InTime t={r.adr_at} testid={`av-in-${r.symbol}`} /></span>
            <FlowTags m={m} testid={`av-${r.symbol}`} />
            {hot?.top?.includes(r.sector) && <span className={`${tag} bg-emerald-600 text-white`}>TOP</span>}
            {hot?.bottom?.includes(r.sector) && <span className={`${tag} bg-rose-600 text-white`}>WORST</span>}
            {r.beiv && <span className={`${tag} ${r.beiv.stars === 3 ? "bg-[#00E676]" : "bg-[#FFD600]"} text-black`} title={`Blue Elephant ${r.beiv.stars === 3 ? "green" : "yellow"} daily candle (${r.beiv.when === "now" ? "today" : "last session"}) · ${r.beiv.x}× its 20-day volume average`} data-testid={`av-beiv-${r.symbol}`}>BE {r.beiv.x}×{r.beiv.when === "last" ? " prev" : ""}</span>}
            {(r.zone || []).map((z) => <span key={z} className={`${tag} bg-violet-500/15 text-violet-300 border border-violet-500/50`} data-testid={`av-zone-${z}-${r.symbol}`}>{ZONE[z]}</span>)}
            <span className="block text-[9px] text-slate-500 font-normal">{r.sector} · RM {inr(r.adr_level)} @ {r.adr_at} · D {V} {inr(r.d_level)} · W {inr(r.w_level)} · M {inr(r.m_level)}</span>
          </span>
          <span className="w-28 text-right" data-testid={`av-open-${side}-${r.symbol}`}>
            <span className="text-slate-700">{r.open_vol ? fmtV(r.open_vol) : "—"}</span>
            {r.open_x != null && <span className={`${tag} ${r.open_x >= 3 ? "bg-amber-400 text-black" : r.open_x >= 2 ? "bg-amber-500/20 text-amber-300 border border-amber-500/50" : "text-slate-500"}`}>{r.open_x}×</span>}
          </span>
          <span className="w-20 text-right text-slate-800">{inr(r.ltp)}</span>
          <span className="w-14 text-right">{pct(r.change_pct)}</span>
        </button>
      </StockMenu>
      <WatchAdd instrumentId={r.instrument_id} symbol={r.symbol} />
    </div>
  );
};

const AdrValue = () => {
  const [d, setD] = useState(null);
  const [open3, setOpen3] = useState(false);
  const [coil, setCoil] = useState(false);
  const [be, setBe] = useState(false);
  useEffect(() => {
    const load = () => api.get("/today/adr-value").then((r) => setD(r.data)).catch(() => setD({ lists: {} }));
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, []);
  const flow = useFlow();
  const keep = (rows = []) => flow.filter(rows).filter((r) => (!open3 || r.open_x >= 3) && (!coil || r.zone?.length) && (!be || r.beiv));
  const up = keep(d?.lists?.up), down = keep(d?.lists?.down);
  return (
    <div className="space-y-3" data-testid="av-panel">
      <div className="flex flex-wrap items-center gap-1">
        <button onClick={() => setOpen3(!open3)} aria-pressed={open3} className={chip(open3)} data-testid="av-open3-toggle">Opening 3×+ only</button>
        <button onClick={() => setCoil(!coil)} aria-pressed={coil} className={chip(coil)} data-testid="av-coil-toggle">Compression (NZ) only</button>
        <button onClick={() => setBe(!be)} aria-pressed={be} className={chip(be)} data-testid="av-beiv-toggle">Blue Elephant only</button>
        <span className="text-[10px] font-mono text-slate-500 ml-2">session {d?.session || "—"}</span>
        <button onClick={() => copyTv([...up, ...down])} data-testid="av-copy-tv" className="ml-auto h-7 px-3 text-xs font-mono border border-slate-300 text-slate-800 hover:border-blue-500 transition-colors flex items-center gap-1.5"><Copy className="h-3.5 w-3.5 text-blue-600" /> Copy to TradingView</button>
      </div>
      {!d ? <p className="p-6 text-xs font-mono text-slate-500">Loading…</p> : (
        <div className="grid gap-3 xl:grid-cols-2">
          {[["up", up], ["down", down]].map(([side, rows]) => (
            <div key={side} className="border border-slate-200 bg-white" data-testid={`av-list-${side}`}>
              <div className={`flex items-center gap-1.5 px-2 h-9 text-xs font-bold font-mono border-b border-slate-200 ${side === "up" ? "text-emerald-600" : "text-rose-600"}`}>
                {side === "up" ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
                {side === "up" ? "Bullish" : "Bearish"} <span className="text-slate-500 font-normal" data-testid={`av-count-${side}`}>{rows.length}</span>
                <span className="ml-2 text-[10px] font-normal text-slate-500 truncate">{side === "up" ? "broke Range Matrix up · above Daily, Weekly & Monthly Node-H" : "broke Range Matrix down · below Daily, Weekly & Monthly Node-L"}</span>
              </div>
              <div className="flex gap-2 px-2 py-1 text-[9px] uppercase text-slate-500 font-mono border-b border-slate-200">
                <span className="flex-1">Stock · marks · levels</span><span className="w-28 text-right">Open 5m vol · ×</span><span className="w-20 text-right">Price</span><span className="w-14 text-right">Day %</span><span className="w-7" />
              </div>
              {rows.length ? rows.map((r) => <Row key={r.symbol} r={r} side={side} hot={d.hot} m={flow.marks[r.symbol]} />)
                : <p className="p-4 text-xs font-mono text-slate-500" data-testid={`av-empty-${side}`}>No stock qualified</p>}
            </div>
          ))}
        </div>
      )}
      <p className="text-[10px] font-mono text-slate-500">Only stocks with opening volume or Big Money ≥ 3× · IN = time the stock entered this scanner · NZ = narrow Flow Zone (IN = narrow + inside) on Daily / Weekly / Monthly · BE = Blue Elephant daily candle (yellow 4×+, green 6.5×+; prev = last session) · gold edge = opening candle 3×+ · TOP / WORST = best / worst 2 sectors</p>
    </div>
  );
};

export default function PowerScan() {
  const [tab, setTab] = useState("adr");
  return (
    <div className="space-y-3" data-testid="power-page">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2"><Flame className="h-5 w-5 text-amber-400" /> TFR Power Scan</h1>
        <p className="text-xs text-slate-500 mt-0.5 font-mono">F&O Range Matrix breakouts beyond all TFR Value Nodes · Nifty 500 Blue Elephant institutional volume candles</p>
      </div>
      <div className="flex gap-1" data-testid="power-tabs">
        {[["adr", "Range Matrix + Value Nodes"], ["beiv", "Blue Elephant Volume"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`power-tab-${k}`}
            className={`h-9 px-4 text-xs font-mono font-bold border transition-colors ${tab === k ? "border-amber-400 bg-amber-400/15 text-slate-900" : "border-slate-300 text-slate-500 hover:text-slate-900"}`}>{l}</button>
        ))}
      </div>
      {tab === "adr" ? <AdrValue /> : <BeivPanel />}
    </div>
  );
}
